'use client'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useCallback, useRef } from 'react'
import BookingList from '~/components/booking/mobile/list'
import BookingSearch from '~/components/booking/mobile/search'
import { useBookingFlow, BookingStep, type PaymentStep } from '~/stores/booking-flow'
import PaymentBooking from '~/components/payment'
import OverlayLoading from '~/components/ui/overlay-loading'
import { useBookingCart } from '~/stores/booking-cart'
import { useHotels } from '~/hooks/use-hotels'
import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'
import { calculateDurationOfStay } from '~/lib'
import { useCancelBooking } from '~/hooks/use-booking'
import { DEFAULT_LANGUAGE } from '~/data/language'
import { useHotelAvailability } from '~/hooks/use-hotel-availability'
import { useHotelAvailabilityStore } from '~/stores/hotel-availability'
import { usePaymentData } from '~/hooks/use-payment-data'
import { useAiChannelRooms, useIsAiChannel } from '~/hooks/use-ai-channel-rooms'

export default function BookingMobile() {
  const { hotels, isLoading } = useHotels()
  const searchParams = useSearchParams()
  const { cancelBooking } = useCancelBooking()
  const {
    currentStep,
    selectedHotel,
    checkInDate,
    checkOutDate,
    adults,
    kidsOver6,
    kidsUnder6,
    setCurrentStep,
    setSearchData,
    addRoom,
    setCurrentPaymentStep,
    currentPaymentStep,
    clearBookingData,
  } = useBookingFlow()
  const { locale } = useLocale()
  const t = getTranslation(locale)
  const { items, totalAmount, clearCart, addToCart } = useBookingCart()
  const { availability } = useHotelAvailabilityStore()
  const isAiChannel = useIsAiChannel()
  useAiChannelRooms()
  const { paymentData, setPaymentData } = usePaymentData()
  // Guards the "reset to step 1 on a stale in-progress booking" check below
  // so it only ever acts on the FIRST paymentData resolution of this mount,
  // never on a bookingNo that appears later as a legitimate part of this
  // same session (a fresh booking just created, gen-qr refreshing data).
  const hasCheckedInitialBookingRef = useRef(false)
  const router = useRouter()
  const pathname = usePathname()
  const urlHotelId = searchParams.get('hotelId')
  const urlCheckInDate = searchParams.get('checkInDate')
  const urlCheckOutDate = searchParams.get('checkOutDate')
  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const todayString = today.toLocaleDateString('en-CA')
  const tomorrowString = tomorrow.toLocaleDateString('en-CA')
  const hasPastDate =
    (urlCheckInDate && urlCheckInDate < todayString) ||
    (urlCheckOutDate && urlCheckOutDate < todayString)
  const selectedCheckInDate = hasPastDate ? todayString : urlCheckInDate || checkInDate
  const selectedCheckOutDate = hasPastDate ? tomorrowString : urlCheckOutDate || checkOutDate
  const { refetch } = useHotelAvailability({
    hotelId: urlHotelId || selectedHotel,
    fromDate: selectedCheckInDate,
    toDate: selectedCheckOutDate,
    adults,
    children: kidsOver6 + kidsUnder6,
  })
  const updateURLParams = useCallback(
    (data: any) => {
      const params = new URLSearchParams()

      params.set('hotelId', data.hotelId)
      params.set('checkInDate', data.checkInDate)
      params.set('checkOutDate', data.checkOutDate)
      params.set('adults', data.adults.toString())
      params.set('kidsOver6', data.kidsOver6.toString())
      params.set('kidsUnder6', data.kidsUnder6.toString())
      // Remove roomType param when searching
      // if (data.roomType) {
      //   params.set('roomType', data.roomType)
      // }
      // Use pathname to determine if we need to include locale
      const shouldIncludeLocale =
        (pathname !== '/' && pathname?.startsWith(`/${locale || DEFAULT_LANGUAGE}`)) ||
        pathname === '/'
      const basePath = shouldIncludeLocale ? `/${locale}/booking` : '/booking'
      router.push(`${basePath}?${params.toString()}`, { scroll: false })
    },
    [router, locale, pathname]
  )

  useEffect(() => {
    if (!hasPastDate) return

    const params = new URLSearchParams(searchParams.toString())
    params.set('checkInDate', todayString)
    params.set('checkOutDate', tomorrowString)
    router.replace(`${pathname}?${params.toString()}`, { scroll: false })

    if (urlHotelId) {
      setSearchData({
        hotelId: urlHotelId,
        checkInDate: todayString,
        checkOutDate: tomorrowString,
        adults,
        kidsOver6,
        kidsUnder6,
        roomType: searchParams.get('roomType') || '',
      })
    }
  }, [
    adults,
    hasPastDate,
    kidsOver6,
    kidsUnder6,
    pathname,
    router,
    searchParams,
    setSearchData,
    todayString,
    tomorrowString,
    urlHotelId,
  ])

  useEffect(() => {
    const epayRes = searchParams.get('epayResult')
    const cancelOrder = searchParams.get('isCancelOrder')
    const bookingNo = searchParams.get('bookingNo')
    if (epayRes) {
      if (cancelOrder) {
        cancelBooking({
          hotelId: Number(selectedHotel),
          bookingId: bookingNo || '',
          reason: 'User cancelled the booking',
        })
        const params = new URLSearchParams()
        params.set('hotelId', selectedHotel)
        params.set('checkInDate', checkInDate || '')
        params.set('checkOutDate', checkOutDate || '')
        params.set('adults', String(adults || 1))
        params.set('kidsOver6', String(kidsOver6 || 0))
        params.set('kidsUnder6', String(kidsUnder6 || 0))
        setCurrentStep(BookingStep.LIST)
        window.history.replaceState({}, '', `${window.location.pathname}?${params}`)
      } else {
        setCurrentStep(BookingStep.PAYMENT)
        const step = searchParams.get('step') || '3'
        const stepNumber = Number(step)
        if (stepNumber >= 1 && stepNumber <= 3) {
          setCurrentPaymentStep(stepNumber as PaymentStep)
        }
      }
    } else {
      if (urlHotelId) {
        setCurrentStep(BookingStep.LIST)
      } else {
        setCurrentStep(BookingStep.SEARCH)
      }
      setCurrentPaymentStep(1)
    }
  }, [])

  // Prefill payment data (name/email/phone) from URL if provided — e.g. a
  // marketing/booking link opened before any booking exists. Guarded on
  // `bookingNo` because Step 2 writes its own `customerName` into the URL via
  // replaceState (so it survives a reload) — without this guard, a plain F5
  // while at step 2/3 would re-trigger this block and clobber the
  // in-progress booking's paymentData (bookingNo/cardItems/totalAmount...)
  // with a stripped-down object built from just the URL params. Also wait
  // for paymentData to actually resolve (`!== undefined`) before checking
  // `bookingNo` — while it's still loading from sessionStorage we can't yet
  // tell whether a booking is in progress, so treat "unknown" as "assume
  // there might be one" and skip until it resolves.
  useEffect(() => {
    // Wait for paymentData to resolve before deciding anything below.
    if (paymentData === undefined) return

    if (!hasCheckedInitialBookingRef.current) {
      hasCheckedInitialBookingRef.current = true
      if (paymentData?.bookingNo) {
        // bookingNo was already present the very FIRST time paymentData
        // resolved this mount — i.e. it's left over from before this page
        // load (an abandoned/stale in-progress booking, possibly paired
        // with a stale persisted currentPaymentStep of 2/3). Reset to step
        // 1 so the user doesn't land straight into a stale payment.
        //
        // A bookingNo that only appears LATER — e.g. Step 1 creating a
        // fresh booking and moving to step 2, or gen-qr refreshing
        // paymentData while already on step 2/3 — must NOT hit this
        // branch. This effect re-runs on every paymentData change (see
        // deps below), and without this once-only guard it would fire on
        // every such legitimate update too, forcing currentPaymentStep
        // back to 1 right after nextStep() just advanced it — which is
        // exactly what caused Step 1 -> Step 2 to appear stuck and gen-qr
        // to re-fire repeatedly (Step2 mounting, getting reset, mounting
        // again).
        setCurrentPaymentStep(1)
        return
      }
    }

    const urlCustomerName = searchParams.get('customerName')
    const urlCustomerEmail = searchParams.get('customerEmail')
    const urlCustomerPhone = searchParams.get('customerPhone')

    if (urlCustomerName || urlCustomerEmail || urlCustomerPhone) {
      setPaymentData({
        ...(paymentData || { cancelTime: 0 }),
        customerName: urlCustomerName || paymentData?.customerName || '',
        email: urlCustomerEmail || paymentData?.email || '',
        phone: urlCustomerPhone || paymentData?.phone || '',
        cancelTime: paymentData?.cancelTime ?? 0,
      })
    }
  }, [searchParams, setPaymentData, paymentData, setCurrentPaymentStep])

  // Handle adding rooms from URL when availability is ready
  useEffect(() => {
    const roomsParam = searchParams.get('rooms')
    // ref=ai links are handled by useAiChannelRooms (waits for prices)
    if (!roomsParam || isAiChannel) return
    if (!availability || availability.length === 0) return

    try {
      const rooms = JSON.parse(decodeURIComponent(roomsParam))
      if (Array.isArray(rooms) && rooms.length > 0) {
        // Clear existing cart, then add rooms from availability
        clearCart()

        rooms.forEach((roomData: any) => {
          const availableRoom = availability.find(
            (room: any) => room.roomCode === roomData.roomCode && room.available > 0
          )
          if (availableRoom) {
            const roomAvailability = {
              ...availableRoom,
              roomInfo: {
                ...availableRoom.roomInfo,
                baseAdult: roomData.adults,
                baseChild: roomData.kidsOver6 || 0,
              },
              guests: {
                adults: roomData.adults,
                children6To12: roomData.kidsOver6 || 0,
                childrenUnder6: roomData.kidsUnder6 || 0,
              },
            }
            const customerCode = hotels?.find(
              (h) => h.hotelId === roomAvailability.hotelId
            )?.customerCode
            addToCart(roomAvailability as any, 1, customerCode)
          } else {
            console.warn(`Room type/code ${roomData.roomCode} not found or not available`)
          }
        })
      }
    } catch (error) {
      console.error('Error parsing rooms from URL (mobile):', error)
    }
  }, [availability, addToCart, clearCart, searchParams, isAiChannel])

  const handleSearchSubmit = useCallback(
    async (data: any) => {
      clearBookingData()
      clearCart()
      setSearchData(data)
      updateURLParams(data)
      await refetch({
        hotelId: data.hotelId,
        fromDate: data.checkInDate,
        toDate: data.checkOutDate,
        adults: data.adults,
        children: data.kidsOver6 + data.kidsUnder6,
      })
      setCurrentStep(BookingStep.LIST)
    },
    [setSearchData, setCurrentStep, clearBookingData, clearCart, refetch]
  )

  const handleRoomSelect = useCallback(
    (room: any) => {
      addRoom(room)
      const url = new URL(window.location.href)
      url.searchParams.set('step', '1')
      window.history.replaceState({}, '', url)
      setCurrentStep(BookingStep.PAYMENT)
    },
    [addRoom, setCurrentStep]
  )
  const handleBackToSearch = () => {
    if (currentStep !== BookingStep.SEARCH) {
      setCurrentStep(BookingStep.SEARCH)
    } else {
      router.push(`${process.env.NEXT_PUBLIC_PORTAL_URL}/${locale}`)
    }
  }
  const renderStepContent = () => {
    switch (currentStep) {
      case BookingStep.SEARCH:
        return (
          <BookingSearch
            hotels={hotels}
            onSubmit={handleSearchSubmit}
            initialValues={{
              hotelId: selectedHotel,
              checkInDate: selectedCheckInDate,
              checkOutDate: selectedCheckOutDate,
              adults,
              kidsOver6,
              kidsUnder6,
              roomType: searchParams.get('roomType') || '',
            }}
            onBackToSearch={handleBackToSearch}
          />
        )
      case BookingStep.LIST:
        return (
          <BookingList
            hotelId={selectedHotel}
            checkInDate={selectedCheckInDate}
            checkOutDate={selectedCheckOutDate}
            adults={adults}
            kidsOver6={kidsOver6}
            kidsUnder6={kidsUnder6}
            roomType={searchParams.get('roomType') || ''}
            onRoomSelect={handleRoomSelect}
            onBackToSearch={handleBackToSearch}
            onContinue={() => {
              const url = new URL(window.location.href)
              url.searchParams.set('step', '1')
              window.history.replaceState({}, '', url)
              setCurrentStep(BookingStep.PAYMENT)
            }}
            hotels={hotels}
          />
        )
      case BookingStep.PAYMENT:
        return (
          <PaymentBooking
            hotelId={selectedHotel}
            arrivalDate={selectedCheckInDate}
            departureDate={selectedCheckOutDate}
            onBackToSearch={() => {
              if (currentPaymentStep === 1) {
                const url = new URL(window.location.href)
                url.searchParams.delete('step')
                window.history.replaceState({}, '', url)
                setCurrentStep(BookingStep.SEARCH)
              } else {
                setCurrentStep(BookingStep.PAYMENT)
              }
            }}
            timeIn={selectedCheckInDate}
            timeOut={selectedCheckOutDate}
            nights={calculateDurationOfStay(selectedCheckInDate, selectedCheckOutDate)}
            totalRooms={items.length}
            totalAmount={totalAmount}
          />
        )
      default:
        return null
    }
  }

  if (isLoading) {
    return <OverlayLoading show={true} text={t('search.loadingHotels')} />
  }

  return <div className="container mx-auto h-full">{renderStepContent()}</div>
}
